import { Component } from '@theme/component';

/**
 * Bundle PDP helpers — builder, combo gallery swaps, flat-image column,
 * mobile carousel flats, and zoom-dialog flats. Loaded as a single module
 * wherever any of those surfaces render; each init no-ops when its DOM is absent.
 */

/**
 * @typedef {{ size?: string|null, id: number, available: boolean, any?: boolean }} BundleVariant
 */

/**
 * Decodes HTML entities from an escaped data attribute into readable text.
 * @param {string} value
 * @returns {string}
 */
function decodeHtmlEntities(value) {
  if (!value || !value.includes('&')) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

/**
 * Copy src/srcset/alt from a source image onto one or more target images.
 * Skips no-op src writes to avoid redundant network fetches.
 *
 * @param {Element | null} source
 * @param {(HTMLImageElement | null)[]} targets
 */
function mirrorImage(source, targets) {
  if (!source) return;

  const src = source.getAttribute('src');
  const srcset = source.getAttribute('srcset');
  const alt = source.getAttribute('alt') || '';

  for (const target of targets) {
    if (!target) continue;
    if (src && target.getAttribute('src') !== src) target.setAttribute('src', src);
    if (srcset) target.setAttribute('srcset', srcset);
    else target.removeAttribute('srcset');
    target.alt = alt;
  }
}

/**
 * Bundle builder.
 *
 * Shopper picks one product per row (a `bundle_products` metaobject) plus one shared size,
 * whose options are pre-rendered in Liquid from the first row.
 *
 * A valid selection writes `_bundle_components` into the product form so it adds alongside the
 * bundle parent, with each row's input set to the variant id for the chosen size. Combo gallery
 * swaps also write `_image` for the ATC popup and cart thumbnail. ATC stays disabled until valid.
 *
 * @extends {Component}
 */
class BundleBuilderComponent extends Component {
  /** @type {HTMLInputElement | null} */
  #hiddenInput = null;
  /** @type {HTMLFieldSetElement | null} */
  #sizeFieldset = null;
  /** @type {HTMLElement[]} */
  #rows = [];
  /**
   * Parsed `data-variants` per product input. The attribute is static after
   * render, so caching avoids re-parsing on every size/row combination.
   * @type {WeakMap<HTMLInputElement, BundleVariant[]>}
   */
  #variantsCache = new WeakMap();

  /**
   * Add-to-cart copy shown when the selected size can't be fulfilled by the
   * current selection (from the `inventory_out_of_stock` locale).
   * @type {string}
   */
  #outOfStockText = '';

  connectedCallback() {
    super.connectedCallback();

    this.#hiddenInput = this.querySelector('[data-bundle-input]');
    this.#sizeFieldset = this.querySelector('[data-bundle-sizes]');
    this.#rows = Array.from(this.querySelectorAll('[data-bundle-row]'));
    this.#outOfStockText = decodeHtmlEntities(this.dataset.outOfStockText?.trim() || '');

    this.addEventListener('change', this.#onChange);

    // Populate each row's legend (name/color) from its checked product on load.
    for (const input of this.#selectedProductInputs()) {
      if (input) this.#updateRowLegend(input);
    }

    const checkedSize = this.#checkedSize();
    if (checkedSize) this.#updateSizeLegend(checkedSize);

    this.#updateSizeAvailability();
    this.#updateComponents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-bundle-product]')) {
      this.#updateRowLegend(target);
      this.#updateSizeAvailability();
      this.#updateComponents();
    } else if (target.matches('[data-bundle-size]')) {
      this.#updateSizeLegend(target);
      this.#updateComponents();
    }
  };

  /**
   * Reflects the selected size into the size legend, e.g. "Size: 0".
   *
   * @param {HTMLInputElement} input
   */
  #updateSizeLegend(input) {
    const label = this.#sizeFieldset?.querySelector('[data-bundle-size-value]');
    if (label) label.textContent = input.value;
  }

  /**
   * Reflects the selected product into its row legend. Rows that expose a
   * `[data-bundle-name]` target show the selected bundle's name, and rows with a
   * `[data-bundle-color]` target show the selected product's color (option one),
   * e.g. "Cashmere Dickey: Grey" or "Color: Grey".
   *
   * @param {HTMLInputElement} input
   */
  #updateRowLegend(input) {
    const row = input.closest('[data-bundle-row]');
    if (!row) return;

    const nameEl = row.querySelector('[data-bundle-name]');
    if (nameEl) nameEl.textContent = input.dataset.bundleName ?? '';

    const colorEl = row.querySelector('[data-bundle-color]');
    if (colorEl) colorEl.textContent = input.dataset.color ?? '';
  }

  /** @returns {(HTMLInputElement|null)[]} */
  #selectedProductInputs() {
    return this.#rows.map((row) => row.querySelector('[data-bundle-product]:checked'));
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {BundleVariant[]}
   */
  #variantsFor(input) {
    const cached = this.#variantsCache.get(input);
    if (cached) return cached;

    let variants = [];
    try {
      const parsed = JSON.parse(input.dataset.variants || '[]');
      if (Array.isArray(parsed)) variants = parsed;
    } catch {
      variants = [];
    }

    this.#variantsCache.set(input, variants);
    return variants;
  }

  /**
   * Finds the variant matching the selected size within a product's variants.
   * Falls back to a one-size (`any`) variant when the product has no sizes, so
   * mixed sized/one-size bundles still resolve.
   *
   * @param {HTMLInputElement} input
   * @param {string|null} size
   * @returns {BundleVariant | undefined}
   */
  #variantForSize(input, size) {
    const variants = this.#variantsFor(input);
    if (size == null) return variants.find((variant) => variant.any) ?? variants[0];
    return (
      variants.find((variant) => !variant.any && variant.size === size) ??
      variants.find((variant) => !variant.any && this.#sizeInRange(variant.size, size)) ??
      variants.find((variant) => variant.any)
    );
  }

  /**
   * Whether a numeric size falls within a range like "00-14" or "16-24". Used
   * for products (e.g. dickeys) whose size options are ranges rather than
   * individual sizes: size 8 -> "00-14", size 16 -> "16-24".
   *
   * @param {string|null|undefined} rangeValue
   * @param {string} size
   * @returns {boolean}
   */
  #sizeInRange(rangeValue, size) {
    if (rangeValue == null) return false;
    const match = /^(\d+)\s*-\s*(\d+)$/.exec(rangeValue.trim());
    const value = Number(size);
    if (!match || !Number.isFinite(value)) return false;
    return value >= Number(match[1]) && value <= Number(match[2]);
  }

  /** @returns {HTMLInputElement | null} */
  #checkedSize() {
    return this.#sizeFieldset?.querySelector('[data-bundle-size]:checked') ?? null;
  }

  /**
   * Greys out sizes the current selection can't fulfil — a size is available only when every
   * selected product has a matching available variant. Re-runs on each product change.
   *
   * Unlike default theme behaviour the inputs stay enabled, so an out-of-stock size is still
   * selectable and the add-to-cart button surfaces the out-of-stock copy instead.
   */
  #updateSizeAvailability() {
    if (!this.#sizeFieldset) return;

    const selected = this.#selectedProductInputs();
    const sizeInputs = /** @type {HTMLInputElement[]} */ (
      Array.from(this.#sizeFieldset.querySelectorAll('[data-bundle-size]'))
    );

    for (const sizeInput of sizeInputs) {
      const size = sizeInput.value;
      const available = selected.every((input) => {
        if (!input) return false;
        const variant = this.#variantForSize(input, size);
        return variant != null && variant.available !== false;
      });

      sizeInput.dataset.optionAvailable = available ? 'true' : 'false';
    }
  }

  /**
   * Resolves each row's variant for the selected size, writes it onto the product input, and
   * builds the `_bundle_components` payload (variantId + type + weight, read by the cart
   * transform function). Add-to-cart is gated until every row resolves to an available variant.
   *
   * Out-of-stock sizes stay selectable, so picking one leaves the bundle invalid and the
   * add-to-cart button surfaces the out-of-stock copy.
   */
  #updateComponents() {
    const selected = this.#selectedProductInputs();
    if (!selected.every(Boolean)) return this.#setValid(false);

    const size = this.#checkedSize()?.value ?? null;

    /** @type {{ variantId: string, type: string, weight: number, image: string, size: string, name: string, color: string, url: string }[]} */
    const components = [];
    let valid = true;
    let outOfStock = false;

    for (const input of /** @type {HTMLInputElement[]} */ (selected)) {
      const variant = this.#variantForSize(input, size);

      // Reflect the size-matched variant id onto the checked input's value, so
      // each row's value is the concrete variant for the selected size.
      if (variant && variant.id != null) input.value = String(variant.id);

      if (!variant || variant.id == null || variant.available === false) {
        valid = false;
        // A size is always selected, so an unfulfillable variant here means the
        // chosen size is out of stock for this selection.
        outOfStock = true;
        continue;
      }

      components.push({
        variantId: `gid://shopify/ProductVariant/${variant.id}`,
        type: input.dataset.type ?? '',
        weight: Number(input.dataset.weight) || 0,
        image: input.dataset.image ?? '',
        size: variant.size ?? size ?? '',
        name: input.dataset.name ?? '',
        color: input.dataset.color ?? '',
        url: input.dataset.url ?? '',
      });
    }

    if (!valid) return this.#setValid(false, outOfStock);

    if (this.#hiddenInput) this.#hiddenInput.value = JSON.stringify({ components });
    this.#setValid(true);
  }

  /**
   * @param {boolean} valid
   * @param {boolean} [outOfStock] - Whether the selection is invalid because the
   *   chosen size is out of stock (drives the add-to-cart copy).
   */
  #setValid(valid, outOfStock = false) {
    if (!valid && this.#hiddenInput) this.#hiddenInput.value = '';
    this.#toggleAddToCart(valid);
    this.#updateAddToCartText(outOfStock);
  }

  /**
   * Disables the add-to-cart button(s) until a valid bundle is selected.
   * Only buttons this component disabled are re-enabled, so a button the theme
   * disabled for its own reasons (e.g. sold out) is left untouched.
   *
   * @param {boolean} enabled
   */
  #toggleAddToCart(enabled) {
    const scope = this.closest('.shopify-section') ?? document;
    const buttons = scope.querySelectorAll('.add-to-cart-button');

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;

      if (enabled) {
        if (button.dataset.bundleGated === 'true') {
          button.disabled = false;
          delete button.dataset.bundleGated;
        }
      } else if (!button.disabled) {
        button.disabled = true;
        button.dataset.bundleGated = 'true';
      }
    }
  }

  /**
   * Swaps the add-to-cart label between its default copy and the out-of-stock
   * copy. Targets every add-to-cart button in the section (main + sticky) so
   * both stay in sync. No-ops when no out-of-stock copy was provided.
   *
   * @param {boolean} outOfStock
   */
  #updateAddToCartText(outOfStock) {
    if (!this.#outOfStockText) return;

    const scope = this.closest('.shopify-section') ?? document;
    const inners = scope.querySelectorAll('.add-to-cart-button .add-to-cart-text__content-inner');
    if (!inners.length) return;

    let text = this.#outOfStockText;
    if (!outOfStock) {
      const container = scope.querySelector('add-to-cart-component[data-add-to-cart-default-text]');
      text = decodeHtmlEntities(
        /** @type {HTMLElement | null} */ (container)?.dataset.addToCartDefaultText?.trim() || '',
      );
    }
    if (!text) return;

    for (const inner of inners) inner.textContent = text;
  }
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}

/* -------------------------------------------------------------------------- */
/* Combo images — left gallery column pairing packshots                       */
/* -------------------------------------------------------------------------- */

/**
 * Mirrors the shopper's bundle selection into the leading media-gallery images.
 *
 * Combo packshots come from the parent's `custom.bundle_images` metafield: `combined_products`
 * metaobjects pairing two products with desktop `images` and `mobile_images`. Liquid renders
 * the default pairing plus a lookup map keyed by the two sorted handles joined with "::".
 * Small viewports swap in `mobile_images` wholesale.
 */
function initComboImages() {
  /**
   * @param {Element | null} el
   * @returns {any}
   */
  function parseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || 'null');
    } catch {
      return null;
    }
  }

  /**
   * @param {string} a
   * @param {string} b
   * @returns {string}
   */
  function normalizeKey(a, b) {
    return [a, b]
      .map((handle) => (handle || '').trim().toLowerCase())
      .sort()
      .join('::');
  }

  /**
   * @param {HTMLImageElement} img
   * @param {{ src: string, max?: string, alt?: string }} image
   */
  function setImage(img, image) {
    if (image.src && img.getAttribute('src') !== image.src) {
      // Drop the server-rendered srcset so the browser uses the new single URL.
      img.removeAttribute('srcset');
      img.setAttribute('src', image.src);
    }
    if (image.max) img.setAttribute('data-max-resolution', image.max);
    if (image.alt != null) img.setAttribute('alt', image.alt);
  }

  /**
   * Resolve the desktop + mobile combo image sets for a pairing key. Map entries
   * are `{ images: [...], mobile: [...] }` (both ordered arrays). Legacy shapes —
   * a bare array, or a single `mobile` object — are still accepted.
   * @param {string} key
   * @returns {{ images: { src?: string, max?: string, alt?: string }[], mobile: { src?: string, max?: string, alt?: string }[] }}
   */
  function entryFor(key) {
    const raw = map[key] || map[defaultKey] || null;
    if (!raw) return { images: [], mobile: [] };
    if (Array.isArray(raw)) return { images: raw, mobile: [] };
    const images = Array.isArray(raw.images) ? raw.images : [];
    let mobile = [];
    if (Array.isArray(raw.mobile)) mobile = raw.mobile;
    else if (raw.mobile && typeof raw.mobile === 'object') mobile = [raw.mobile];
    return { images, mobile };
  }

  /**
   * On small viewports use the pairing's dedicated `mobile_images` set (a full
   * parallel swap); otherwise use the desktop `images` set.
   * @param {string} key
   * @returns {{ images: { src?: string, max?: string, alt?: string }[], usedMobile: boolean }}
   */
  function resolveImages(key) {
    const { images, mobile } = entryFor(key);
    const isMobileViewport = window.matchMedia('(max-width: 1079px)').matches;
    if (isMobileViewport && mobile.length) {
      return { images: mobile, usedMobile: true };
    }
    return { images, usedMobile: false };
  }

  const builder = document.querySelector('bundle-builder-component');
  const map = parseJSON(document.querySelector('[data-bundle-combo-map]'));
  const config = parseJSON(document.querySelector('[data-bundle-combo-config]'));
  if (!builder || !map || !config) return;

  const slotIds = (config.slotMediaIds || []).map(String);
  const defaultKey = config.defaultKey || '';
  if (!slotIds.length) return;

  // Combo slot order: 0 = flat packshot, 1 = on-model shot.
  const ON_MODEL_SLOT = 1;
  // The size guide renders its product image at 240px wide, so the gallery-sized
  // combo URLs (1600w) are re-requested at a sane width.
  const SIZE_GUIDE_IMAGE_WIDTH = 600;

  const rows = Array.from(builder.querySelectorAll('[data-bundle-row]')).slice(0, 2);

  function currentKey() {
    const handles = rows.map((row) => {
      const checked = row.querySelector('[data-bundle-product]:checked');
      return checked instanceof HTMLInputElement ? checked.dataset.handle || '' : '';
    });
    if (handles.length < 2 || !handles[0] || !handles[1]) return '';
    return normalizeKey(handles[0], handles[1]);
  }

  /**
   * The i-th thumbnail button across every thumbnail rail (gallery + zoom).
   * @param {number} slotIndex
   * @returns {HTMLElement[]}
   */
  function thumbsAt(slotIndex) {
    const lists = document.querySelectorAll(
      '.slideshow-controls__thumbnails, .dialog-thumbnails-list',
    );
    /** @type {HTMLElement[]} */
    const out = [];
    lists.forEach((list) => {
      const button = list.querySelectorAll('button')[slotIndex];
      if (button instanceof HTMLElement) out.push(button);
    });
    return out;
  }

  /**
   * @param {Element} el
   * @param {boolean} hide
   */
  function toggleHidden(el, hide) {
    if (hide) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
  }

  /**
   * @param {number} slotIndex
   * @param {{ src: string, max?: string, alt?: string } | undefined} image
   * @param {boolean} preserveInitial - keep the server-rendered (responsive) markup
   */
  function applySlot(slotIndex, image, preserveInitial) {
    const hide = !image;
    const mediaId = slotIds[slotIndex];

    document.querySelectorAll(`[data-media-id="${mediaId}"]`).forEach((container) => {
      if (!preserveInitial && image) {
        const img = container.querySelector('img.product-media__image');
        if (img instanceof HTMLImageElement) setImage(img, image);
      }
      const slot = container.closest('slideshow-slide, li');
      if (slot) toggleHidden(slot, hide);
    });

    thumbsAt(slotIndex).forEach((thumb) => {
      if (!preserveInitial && image) {
        const timg = thumb.querySelector('img');
        if (timg instanceof HTMLImageElement) setImage(timg, image);
      }
      toggleHidden(thumb, hide);
    });
  }

  /**
   * @param {{ src?: string }[]} images
   */
  function syncCarouselControls(images) {
    const media = document.querySelector('.product-information__media--bundle');
    const slideshow = media && media.querySelector('slideshow-component');
    if (!slideshow) return;

    const segments = slideshow.querySelectorAll('.slideshow-controls__progress-bar-segment');
    const dots = slideshow.querySelectorAll('.slideshow-controls__dots > li');

    slotIds.forEach((/** @type {string} */ slotId, /** @type {number} */ i) => {
      const hide = !images[i];

      const slideMedia = slideshow.querySelector(`[data-media-id="${slotId}"]`);
      const slide = slideMedia ? slideMedia.closest('slideshow-slide') : null;
      if (slide) {
        if (hide) slide.removeAttribute('ref');
        else if (slide.getAttribute('ref') !== 'slides[]') slide.setAttribute('ref', 'slides[]');
      }

      const segment = segments[i];
      if (segment instanceof HTMLElement) {
        toggleHidden(segment, hide);
        if (hide) {
          segment.removeAttribute('ref');
          segment.setAttribute('aria-current', 'false');
        } else if (segment.getAttribute('ref') !== 'progressBarSegments[]') {
          segment.setAttribute('ref', 'progressBarSegments[]');
        }
      }

      const dot = dots[i];
      if (dot instanceof HTMLElement) {
        toggleHidden(dot, hide);
        const button = dot.querySelector('button');
        if (button) {
          if (hide) {
            button.removeAttribute('ref');
            button.setAttribute('aria-current', 'false');
          } else if (button.getAttribute('ref') !== 'dots[]') {
            button.setAttribute('ref', 'dots[]');
          }
        }
      }
    });

    // Refresh the slideshow's ref arrays now the ref attributes have changed so
    // `progressBarSegments`/`dots` reflect only the visible controls.
    if (typeof (/** @type {any} */ (slideshow).updatedCallback) === 'function') {
      /** @type {any} */ (slideshow).updatedCallback();
    }
  }

  /**
   * Keep `properties[_image]` in sync with the first gallery combo slot (flat)
   * so the ATC success popup and cart thumbnail match the shopper's selection.
   * @param {{ src?: string }[]} images
   */
  function syncImageProperty(images) {
    const imageInput = document.querySelector('[data-bundle-image-input]');
    if (!(imageInput instanceof HTMLInputElement)) return;
    imageInput.value = images[0]?.src || '';
  }

  /**
   * Re-request a Shopify CDN image URL at a different width.
   * @param {string} src
   * @param {number} width
   * @returns {string}
   */
  function atWidth(src, width) {
    return src.replace(/([?&]width=)\d+/, `$1${width}`);
  }

  /**
   * Point the size-guide trigger at the on-model combo image of the current
   * pairing — the same shot the shopper sees in gallery slot 2 — rather than the
   * parent product's featured image, which the bundle gallery never displays.
   * @param {{ src?: string }[]} images
   */
  function syncSizeGuideImage(images) {
    const link = builder.querySelector('[data-open-size-guide]');
    if (!link) return;
    const src = images[ON_MODEL_SLOT]?.src || images[0]?.src || '';
    link.setAttribute('data-product-image', src ? atWidth(src, SIZE_GUIDE_IMAGE_WIDTH) : '');
  }

  /**
   * After a selection change, bring the updated first combo image into view:
   * desktop scrolls the page to the top of the left gallery column; mobile
   * resets the media carousel to the first slide.
   */
  function revealFirstGalleryImage() {
    const mediaRoot = document.querySelector('.product-information__media--bundle');
    if (!mediaRoot) return;

    const isDesktop = window.matchMedia('(min-width: 1080px)').matches;

    if (isDesktop) {
      const mediaId = slotIds[0];
      const firstSlot = mediaId
        ? mediaRoot.querySelector(`.media-gallery__grid [data-media-id="${mediaId}"]`)
        : mediaRoot.querySelector('.media-gallery__grid .product-media-container');
      if (!(firstSlot instanceof HTMLElement)) return;

      // Offset for the sticky header so the first image isn't tucked underneath.
      const headerOffset =
        Number.parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        ) || 0;
      const top = firstSlot.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      return;
    }

    const slideshow = /** @type {{ select?: Function } | null} */ (
      mediaRoot.querySelector('slideshow-component')
    );
    if (typeof slideshow?.select === 'function') {
      slideshow.select(0, undefined, { animate: false });
    }
  }

  /** @param {boolean} isInitial */
  function update(isInitial) {
    const key = currentKey();
    const { images, usedMobile } = resolveImages(key);

    // On load the default pairing is already server-rendered with full
    // responsive markup, so leave those images untouched (avoids a needless
    // downgrade to the single-width swap URL) and only reconcile visibility —
    // unless the mobile_images set must replace the whole desktop set.
    const preserveInitial = Boolean(isInitial) && key === defaultKey && !usedMobile;

    slotIds.forEach((/** @type {string} */ _slotId, /** @type {number} */ i) =>
      applySlot(i, images[i], preserveInitial),
    );
    syncCarouselControls(images);
    syncImageProperty(images);
    syncSizeGuideImage(images);

    if (!isInitial) revealFirstGalleryImage();
  }

  builder.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('[data-bundle-product]')) {
      update(false);
    }
  });

  // Re-resolve when crossing the mobile/desktop breakpoint so mobile_images swaps apply.
  const viewportMq = window.matchMedia('(max-width: 1079px)');
  const onViewportChange = () => update(false);
  if (typeof viewportMq.addEventListener === 'function') {
    viewportMq.addEventListener('change', onViewportChange);
  } else if (typeof viewportMq.addListener === 'function') {
    viewportMq.addListener(onViewportChange);
  }

  update(true);
}

/* -------------------------------------------------------------------------- */
/* Flat images — right-hand desktop column                                    */
/* -------------------------------------------------------------------------- */

/**
 * Mirrors the shopper's bundle selection into the right-hand gallery column:
 * each [data-bundle-flat-slot="i"] shows the flat image of the product checked
 * in bundle row i.
 */
function initFlatImages() {
  const flats = document.querySelector('[data-bundle-flats]');
  const builder = document.querySelector('bundle-builder-component');
  if (!flats || !builder) return;

  const rows = Array.from(builder.querySelectorAll('[data-bundle-row]'));

  function updateSlot(row, index) {
    const checked = row.querySelector('[data-bundle-product]:checked');
    const slot = flats.querySelector(`[data-bundle-flat-slot="${index}"]`);
    if (!checked || !slot) return;

    const img = slot.querySelector('.bundle-pdp-flats__image');
    if (!img) return;

    const src = checked.dataset.imageLarge || checked.dataset.image;
    if (!src || img.getAttribute('src') === src) return;

    // Drop the server-rendered srcset so the browser uses the new single URL.
    img.removeAttribute('srcset');
    img.src = src;
    img.alt = checked.dataset.name || '';
  }

  function update() {
    rows.forEach(updateSlot);
  }

  builder.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('[data-bundle-product]')) {
      update();
    }
  });

  // Sync once on load in case the server default and checked radio ever differ.
  update();
}

/* -------------------------------------------------------------------------- */
/* Mobile flats — append packshots to the media carousel                      */
/* -------------------------------------------------------------------------- */

/**
 * Appends a single carousel slide after the parent's media that shows every
 * bundle-row flat side by side, and extends the progress bar by one segment.
 * The slide only surfaces on mobile (carousel is desktop-hidden). Each half
 * mirrors its [data-bundle-flat-slot] source image.
 */
function initMobileFlats() {
  /**
   * @param {Element} templateSlide
   * @param {Element[]} flatItems
   * @param {number} index
   * @returns {HTMLElement}
   */
  function buildCombinedSlide(templateSlide, flatItems, index) {
    const slide = /** @type {HTMLElement} */ (templateSlide.cloneNode(true));

    slide.setAttribute('data-bundle-flat-carousel-combo', '');
    slide.setAttribute('aria-hidden', 'true');
    slide.removeAttribute('on:click');
    slide.removeAttribute('data-view-transition-type');
    slide.removeAttribute('slide-id');
    slide.classList.remove('media-fit-cover', 'product-media-container--zoomable');
    slide.classList.add('media-fit-contain');

    // Point the slide's own view-timeline at its new index and switch to contain.
    const style = (slide.getAttribute('style') || '')
      .replace(/--slideshow-timeline:\s*--slide-\d+/, `--slideshow-timeline: --slide-${index}`)
      .replace(/--product-media-fit:\s*cover/, '--product-media-fit: contain');
    slide.setAttribute('style', style);

    const productMedia = slide.querySelector('.product-media');
    if (!productMedia) return slide;

    // The cloned slide wraps its media in the shared single-image
    // <drag-zoom-wrapper>, whose zoom transform is applied to every descendant
    // .product-media__image via CSS custom properties set on the wrapper — so
    // one gesture would scale both flats together. Replace it with a plain div
    // (keeping the class for layout only) and give each flat its own
    // <drag-zoom-wrapper> below, so per-flat zoom state is fully independent.
    const sharedWrapper = slide.querySelector('drag-zoom-wrapper.product-media__drag-zoom-wrapper');
    if (sharedWrapper) {
      const layout = document.createElement('div');
      layout.className = sharedWrapper.className;
      layout.replaceChildren(...sharedWrapper.childNodes);
      sharedWrapper.replaceWith(layout);
    }

    productMedia.removeAttribute('data-media-id');

    const combo = document.createElement('div');
    combo.className = 'bundle-pdp-flats-carousel';

    for (const flatItem of flatItems) {
      const slot = flatItem.getAttribute('data-bundle-flat-slot');
      const flatImg = flatItem.querySelector('img');
      // Each flat cell is its own drag-zoom-wrapper so pinch/pan/double-tap and
      // the constraint math run per flat (the element upgrades + initialises
      // when the slide is appended to the live carousel).
      const cell = document.createElement('drag-zoom-wrapper');
      cell.className = 'bundle-pdp-flats-carousel__item';
      if (slot != null) cell.setAttribute('data-bundle-flat-carousel-slot', slot);

      if (flatImg) {
        const img = /** @type {HTMLImageElement} */ (flatImg.cloneNode(true));
        img.className = 'product-media__image';
        img.setAttribute('loading', 'lazy');
        cell.appendChild(img);
      }

      combo.appendChild(cell);
    }

    productMedia.replaceChildren(combo);
    return slide;
  }

  /**
   * @param {number} total
   * @returns {string}
   */
  function timelineScope(total) {
    const scope = [];
    for (let i = 1; i <= total; i += 1) scope.push(`--slide-${i}`);
    return scope.join(', ');
  }

  const media = document.querySelector('.product-information__media--bundle');
  if (!media) return;

  const slideshow = media.querySelector('slideshow-component');
  const flats = document.querySelector('[data-bundle-flats]');
  if (!slideshow || !flats) return;
  if (slideshow.querySelector('[data-bundle-flat-carousel-combo]')) return; // already built

  const scroller = slideshow.querySelector('slideshow-slides');
  const templateSlide = scroller && scroller.querySelector('slideshow-slide');
  const flatItems = Array.from(flats.querySelectorAll('[data-bundle-flat-slot]'));
  if (!scroller || !templateSlide || !flatItems.length) return;

  const track = slideshow.querySelector('.slideshow-controls__progress-bar-track');
  const templateSeg = track && track.querySelector('.slideshow-controls__progress-bar-segment');
  const baseCount = scroller.querySelectorAll('slideshow-slide').length;
  const comboIndex = baseCount;

  scroller.appendChild(buildCombinedSlide(templateSlide, flatItems, comboIndex));

  if (track && templateSeg) {
    const seg = /** @type {HTMLElement} */ (templateSeg.cloneNode(false));
    seg.setAttribute('data-slide-index', String(comboIndex));
    seg.setAttribute('aria-current', 'false');
    seg.setAttribute('data-bundle-flat-segment', '');
    track.appendChild(seg);
  }

  const total = baseCount + 1;
  slideshow.style.setProperty('--slideshow-timeline', timelineScope(total));
  const progressBar = slideshow.querySelector('.slideshow-controls__progress-bar');
  if (progressBar) progressBar.style.setProperty('--total-slides', String(total));

  // Register the appended slide + progress segment with the slideshow's refs.
  // Do NOT reconnect the element: disconnectedCallback destroys the Scroller without clearing
  // its reference, so #setupSlideshow() early-returns on reconnect and never rebuilds the
  // scroll listener — silently breaking swipe tracking. updatedCallback() refreshes refs
  // synchronously while leaving the live Scroller alone.
  if (typeof (/** @type {any} */ (slideshow).updatedCallback) === 'function') {
    /** @type {any} */ (slideshow).updatedCallback();
  }

  // Keep each half of the combined slide in sync with its source flat image.
  for (const flatItem of flatItems) {
    const slot = flatItem.getAttribute('data-bundle-flat-slot');
    const source = flatItem.querySelector('img');
    const target = slideshow.querySelector(`[data-bundle-flat-carousel-slot="${slot}"] img`);
    if (!source || !target) continue;

    mirrorImage(source, [target]);
    const observer = new MutationObserver(() => mirrorImage(source, [target]));
    observer.observe(source, {
      attributes: true,
      attributeFilter: ['src', 'srcset', 'alt'],
    });
  }
}

/* -------------------------------------------------------------------------- */
/* Flats zoom — wire desktop flats into the product zoom dialog               */
/* -------------------------------------------------------------------------- */

/**
 * Extends the parent product's <zoom-dialog> so desktop flats become part of
 * the fullscreen zoom experience. Indices append after the product's own media.
 * (Mobile carousel combines both flats into one slide; zoom keeps them separate.)
 */
function initFlatsZoom() {
  /**
   * @param {Element} templateLi
   * @param {string} slot
   * @returns {{ li: HTMLElement, img: HTMLImageElement | null }}
   */
  function buildMediaSlide(templateLi, slot) {
    const li = /** @type {HTMLElement} */ (templateLi.cloneNode(true));
    li.id = `bundle-flat-zoom-${slot}`;
    li.setAttribute('data-bundle-flat-zoom-slot', slot);

    // Drop hooks that must not be duplicated from the source media.
    li.querySelectorAll('[data-media-id]').forEach((el) => el.removeAttribute('data-media-id'));

    const img = /** @type {HTMLImageElement | null} */ (li.querySelector('.product-media__image'));
    if (img) {
      img.removeAttribute('data_max_resolution');
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.setAttribute('loading', 'lazy');
    }

    return { li, img };
  }

  /**
   * @param {Element} templateThumb
   * @param {number} index
   * @param {string} slot
   * @returns {{ btn: HTMLElement, img: HTMLImageElement | null }}
   */
  function buildThumbnail(templateThumb, index, slot) {
    const btn = /** @type {HTMLElement} */ (templateThumb.cloneNode(true));
    btn.removeAttribute('aria-selected');
    btn.setAttribute('data-bundle-flat-zoom-slot', slot);
    btn.setAttribute('on:click', `/handleThumbnailClick/${index}`);
    btn.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);

    return { btn, img: btn.querySelector('img') };
  }

  const flats = document.querySelector('[data-bundle-flats]');
  const zoomDialog = /** @type {(Element & { updatedCallback?: () => void }) | null} */ (
    document.querySelector('zoom-dialog')
  );
  if (!flats || !zoomDialog) return;

  const gallery = zoomDialog.querySelector('.dialog-zoomed-gallery');
  const thumbnails = zoomDialog.querySelector('[ref="thumbnails"]');
  if (!gallery) return;
  if (gallery.querySelector('[data-bundle-flat-zoom-slot]')) return; // already built

  const templateLi = gallery.querySelector('li.product-media-container--image');
  if (!templateLi) return;
  const templateThumb = thumbnails
    ? thumbnails.querySelector('.dialog-thumbnails-list__thumbnail')
    : null;

  const baseCount = gallery.querySelectorAll('li').length;
  const flatItems = Array.from(flats.querySelectorAll('[data-bundle-flat-slot]'));

  flatItems.forEach((flatItem, i) => {
    const slot = flatItem.getAttribute('data-bundle-flat-slot') || String(i);
    const sourceImg = flatItem.querySelector('.bundle-pdp-flats__image');
    if (!sourceImg) return;

    const index = baseCount + i;

    const { li, img: zoomImg } = buildMediaSlide(templateLi, slot);
    let thumbImg = null;
    if (templateThumb && thumbnails) {
      const { btn, img } = buildThumbnail(templateThumb, index, slot);
      thumbnails.appendChild(btn);
      thumbImg = img;
    }

    const targets = [zoomImg, thumbImg];
    mirrorImage(sourceImg, targets);
    gallery.appendChild(li);

    const observer = new MutationObserver(() => mirrorImage(sourceImg, targets));
    observer.observe(sourceImg, { attributes: true, attributeFilter: ['src', 'srcset', 'alt'] });

    const button = flatItem.querySelector('.bundle-pdp-flats__zoom-button');
    if (button) {
      button.setAttribute('on:click', `#${zoomDialog.id}/open/${index}`);
      button.removeAttribute('hidden');
    }
  });

  // The dialog captured its refs at connect time (before these appends), so force a
  // refresh — the intended hook for external DOM updates — so refs.media/thumbnails
  // include the flats and open(index) can scroll to them.
  if (typeof zoomDialog.updatedCallback === 'function') zoomDialog.updatedCallback();
}

function initBundlePdp() {
  // Flat images first so mobile/zoom mirrors see the current selection.
  initFlatImages();
  initComboImages();
  initMobileFlats();
  initFlatsZoom();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundlePdp);
} else {
  initBundlePdp();
}
